import { Component, OnInit } from '@angular/core'
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute, Router } from '@angular/router'
import moment from 'moment'
import { forkJoin } from 'rxjs'
import { ClientDto } from 'src/app/models/client/client-dto'
import { CheckBoxData } from 'src/app/models/reddi/checkbox-data.model'
import { SlatAssessmentDto } from 'src/app/models/slat/slat-assessment-dto'
import { SlatAssessmentItemDto } from 'src/app/models/slat/slat-assessment-item-dto'
import { SlatAssessmentTableGroupByDto } from 'src/app/models/slat/slat-assessment-table-group-dto'
import { SlatAssessmentTableItemsDto } from 'src/app/models/slat/slat-assessment-table-items-dto'
import { BannerService } from 'src/app/services/banner/banner.service'
import { ClientService } from 'src/app/services/client/client.service'
import { CommonService } from 'src/app/services/common/common.service'
import { LogService } from 'src/app/services/logger/log.service'
import { PreviousRouteService } from 'src/app/services/previous-route/previous-route.service'
import { ImpactingFactor } from 'src/app/services/reddi/reddi-utils'
import { SlatService } from 'src/app/services/slat/slat.service'
import { SpinnerService } from 'src/app/services/spinner/spinner.service'
import { AreYouSureModalComponent } from '../../complex/modals/are-you-sure-modal/are-you-sure-modal.component'
import { SlatEditModalComponent } from '../../complex/modals/slat-edit-modal/slat-edit-modal.component'
import { TranslateService } from '@ngx-translate/core'
import { SlatAssessmentStatusModalComponent } from '../../complex/modals/slat-assessment-status-modal/slat-assessment-status-modal.component'
import { SlatAssessmentStatus } from '../../../models/slat/slat-assessment-status'
import { formatDate } from '@angular/common'

@Component({
  selector: 'app-slat-assessment',
  templateUrl: './slat-assessment.component.html',
  styleUrl: './slat-assessment.component.scss',
  standalone: false,
})
export class SlatAssessmentComponent implements OnInit {
  slatTableData: (
    | SlatAssessmentTableItemsDto
    | SlatAssessmentTableGroupByDto
  )[] = []
  slatAssessmentDataDto: SlatAssessmentDto

  displayedColumnsTest: string[] = [
    'Name',
    'RawScore',
    'Score',
    'PercentileRank',
    'Level',
    'Action',
  ]
  dataSource = this.slatTableData
  public _considerations: CheckBoxData[] = [
    new CheckBoxData(
      9,
      'Confirmed middle ear pathology',
      ImpactingFactor.ConfirmedMiddleEarPathology,
      false,
    ),
    new CheckBoxData(
      10,
      'Suspected middle ear pathology',
      ImpactingFactor.SuspectedMiddleEarPathology,
      false,
    ),
    new CheckBoxData(
      11,
      'Child unsettled / unwell',
      ImpactingFactor.ChildUnsettledOrUnwell,
      false,
    ),
    new CheckBoxData(
      12,
      'Background noise present',
      ImpactingFactor.BackgroundNoisePresent,
      false,
    ),
  ]
  slatForm: FormGroup = new FormGroup({
    assessmentDate: new FormControl<moment.Moment>(null, [
      Validators.required,
      this.futureDateValidator(),
    ]),
    notes: new FormControl<string>('', [Validators.maxLength(2000)]),
    abbreviatedName: new FormControl<string>('', [Validators.required]),
    considerations: this.formBuilder.array(
      this._considerations.map(() => this.formBuilder.control(false)),
    ),
  })
  _slatId: string
  _clientId: string
  _clientName: string = 'Loading...'
  _title: string = '\u00A0'
  _age: string
  _dateOfBirth: Date = new Date()
  _showClientDetails: boolean = false
  _presenterName: string
  _tableColumnsToDisplay: string[] = []
  _client: ClientDto
  currentStepIndex: number = 0
  _dashboardUrl: string
  _totalReviews = 4
  _hasAccess: any = {
    slatHasAccess: false,
    flipHasAccess: false,
    reddiHasAccess: false,
  }
  _isError: boolean = false
  _isSubmitted: boolean = false
  private _selectedConsiderationOptions: ImpactingFactor[]

  constructor(
    public formBuilder: FormBuilder,
    public router: Router,
    public spinnerService: SpinnerService,
    public activatedRouter: ActivatedRoute,
    public clientService: ClientService,
    public bannerService: BannerService,
    public slatService: SlatService,
    public dialog: MatDialog,
    public previousRouteService: PreviousRouteService,
    private logger: LogService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  isGroup(index: number, item: any): boolean {
    return item.isGroupBy
  }

  ngOnInit(): void {
    this.initializeClientData()

    this.translateService.onLangChange.subscribe(() => {
      this.initializeClientData()
    })
  }
  private initializeClientData(): void {
    this.spinnerService.show()
    this._clientId = this.activatedRouter.snapshot.url[2].path
    this._slatId = this.activatedRouter.snapshot.url[3]?.path
    this._dashboardUrl = `slat/history/${this._clientId}`

    const request = {
      client: this.clientService.Details(this._clientId),
      slatAssessment:
        this._slatId !== undefined
          ? this.slatService.GetAssessmentInstanceById(this._slatId)
          : this.slatService.GetAssessment(this._clientId, '0'),
    }
    forkJoin(request).subscribe({
      next: ({ client, slatAssessment }) => {
        this._client = client
        this._clientName = client?.preferredFullName
        this._age = client?.age
        this._dateOfBirth = client?.dateOfBirth
        this._title = client?.preferredFullName
        this._presenterName =
          slatAssessment?.presenterName ?? client?.clientCaseManagerName
        this._showClientDetails = true
        this.checkToolsAccess()
        this.checkIndicators()
        this.slatAssessmentDataDto =
          this.slatService.assignHeaders(slatAssessment)
        this.slatAssessmentDataDto = this.slatService.updateLevelItem(
          this.slatAssessmentDataDto,
        )
        this.processSidebarComponents(slatAssessment)
        this.refreshTableData()
      },
      error: () => {
        this.bannerService.showBanner(
          this.commonService.translateSafe('Unable to retrieve data'),
          'error',
          30000,
        )
        this.spinnerService.hide()
      },
      complete: () => {
        this.spinnerService.hide()
      },
    })
  }

  getConsiderations(index: number, event: Event) {
    const checkbox = event.target as HTMLInputElement
    this._considerations[index].checked = checkbox.checked

    const considerationsArray = this.slatForm.get('considerations') as FormArray
    considerationsArray.at(index).setValue(checkbox.checked)

    // If you need to get all selected considerations
    this._selectedConsiderationOptions = this._considerations
      .filter((c) => c.checked)
      .map((c) => c.value)
  }

  openEditModal(element: any, index: number): void {
    const editableItems = this.slatTableData.filter(
      (x) => !x.isGroupBy,
    ) as SlatAssessmentItemDto[]

    let idx = element.isCore ? index - 2 : index - 1

    const dialogRef = this.dialog.open(SlatEditModalComponent, {
      width: '55%',
      data: {
        items: editableItems,
        currentIndex: idx,
      },
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.updatedItems?.length) {
        for (const updatedItem of result.updatedItems) {
          this.slatAssessmentDataDto = this.updateValue(
            this.slatAssessmentDataDto,
            updatedItem.templateId,
            updatedItem.groupId,
            updatedItem.itemId,
            updatedItem,
          )
        }
        this.refreshTableData()
      }
    })
  }

  private isAssessmentItem(
    item: SlatAssessmentTableItemsDto | SlatAssessmentTableGroupByDto,
  ): item is SlatAssessmentTableItemsDto {
    return (item as SlatAssessmentTableItemsDto).itemId !== undefined
  }

  refreshTableData = () => {
    const rawData = this.slatService.SlatFormatMultiHeadingData(
      this.slatAssessmentDataDto,
    )

    this.slatTableData = rawData.map((item) => {
      if (this.isAssessmentItem(item)) {
        // It's an individual item
        return {
          ...item,
          name: this.commonService.translateSafe(`${item.name}`),
          description: this.commonService.translateSafe(`${item.description}`),
          level: this.commonService.translateSafe(`${item.level}`),
          groupName: this.commonService.translateSafe(`${item.groupName}`),
        }
      } else {
        // It's a group row — translate only what’s needed
        return {
          ...item,
          groupName: this.commonService.translateSafe(`${item.groupName}`),
        }
      }
    })

    this.dataSource = this.slatTableData
  }

  updateValue = (
    data: SlatAssessmentDto,
    templateId: string,
    groupId: string,
    itemId: string,
    updates: Partial<SlatAssessmentItemDto>,
  ): SlatAssessmentDto => ({
    ...data,
    groups: (data.groups ?? []).map((group) =>
      group.templateId === templateId && group.groupId === groupId
        ? {
            ...group,
            items: (group.items ?? []).map((item) =>
              item.itemId === itemId ? { ...item, ...updates } : item,
            ),
          }
        : group,
    ),
  })

  finishSession(): void {
    // this._isError = this.checkForOneSubTest()
    if (this.slatForm.invalid || this._isError) {
      this.slatForm.markAllAsTouched()
      this.slatAssessmentDataDto.finishingSession = false
      this._isSubmitted = true
      return // Exit the function if form is not valid
    }
    this.slatAssessmentDataDto.clientId = this._clientId
    this.slatAssessmentDataDto.assessmentDate = new Date(
      moment(this.slatForm.controls['assessmentDate'].value).format(
        'YYYY-MM-DD',
      ),
    )
    this.slatAssessmentDataDto.impactingFactors =
      this._selectedConsiderationOptions
    this.slatAssessmentDataDto.notes = this.slatForm.controls['notes'].value
    this.slatAssessmentDataDto.assessmentName =
      moment(this.slatForm.controls['assessmentDate'].value).format(
        'DD-MM-YYYY',
      ) +
      ' - ' +
      this.slatAssessmentDataDto.name

    const dialogRef = this.dialog.open(SlatAssessmentStatusModalComponent, {
      width: '605px',
      height: '316px',
      panelClass: 'slat-assessment-status-panel',
      data: {
        selectedStatus:
          this.slatAssessmentDataDto?.assessmentStatus ??
          SlatAssessmentStatus.Completed,
      } as { selectedStatus: SlatAssessmentStatus },
    })

    dialogRef
      .afterClosed()
      .subscribe((status: SlatAssessmentStatus | null | undefined) => {
        if (status === null || status === undefined) {
          // User cancelled; do not submit
          this.slatAssessmentDataDto.finishingSession = false
          return
        }

        // Mark the assessment as submitted
        this.slatAssessmentDataDto.assessmentStatus = status
        this.slatAssessmentDataDto.finishingSession = true

        this.spinnerService.show()
        this.slatService.Submit(this.slatAssessmentDataDto).subscribe({
          next: (response: any) => {
            this.logger.log(
              'Submit Assessment completed successfully',
              'success',
              this.slatAssessmentDataDto,
            )
          },
          error: (error: any) => {
            this.spinnerService.hide()
            this.bannerService.showBanner(
              this.commonService.translateSafe(
                'Error creating SLAT Assessment',
              ),
              'error',
              5000,
            )
            throw error
          },
          complete: () => {
            this.spinnerService.hide()
            this.bannerService.showBanner(
              this.commonService.translateSafe(
                'SLAT Assessment Created Successfully',
              ),
              'success',
              5000,
            )
            this.slatForm.markAsPristine()
            this.router.navigateByUrl(this._dashboardUrl, {
              state: { startAutoRefresh: true },
            })
          },
        })
      })
  }

  cancelAssessment(): void {
    const dialogRef = this.dialog.open(AreYouSureModalComponent, {
      width: '605px',
      height: '316px',
      panelClass: 'are-you-sure-panel',
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.router.navigateByUrl(this._dashboardUrl, {
          state: { startAutoRefresh: true },
        })
      } else {
      }
    })
  }

  navigateTo(target: string): void {
    this.commonService.navigateTo(target, this._clientId)
  }

  clientHasAccessToFlip(client: ClientDto): boolean {
    return this.commonService.clientHasAccessToFlip(client)
  }

  processSidebarComponents(slatAssessment: SlatAssessmentDto) {
    this.slatAssessmentDataDto.assessmentDate =
      slatAssessment?.assessmentDate ?? moment().toDate()
    this.slatAssessmentDataDto.notes = slatAssessment?.notes
    this.slatAssessmentDataDto.abbreviatedName = slatAssessment?.abbreviatedName
    for (let i = 0; i < slatAssessment?.impactingFactors?.length; i++) {
      if (slatAssessment?.impactingFactors[i] !== ImpactingFactor.None) {
        const considerationUpdate = this._considerations.find(
          (item: any) => item.value === slatAssessment.impactingFactors[i],
        )
        if (considerationUpdate) {
          considerationUpdate.checked = true
          if (
            !this._considerations.some(
              (item) => item.value === considerationUpdate.value,
            )
          ) {
            this._considerations = [
              ...this._considerations,
              considerationUpdate,
            ]
          }
        }
      } else {
        this._considerations[i].checked = false
      }
    }
    // If you need to get all selected considerations
    this._selectedConsiderationOptions = this._considerations
      .filter((c) => c.checked)
      .map((c) => c.value)
    //set form control values
    this.slatForm.controls['assessmentDate'].setValue(
      moment(this.slatAssessmentDataDto.assessmentDate),
    )
    this.slatForm.controls['abbreviatedName'].setValue(
      this.slatAssessmentDataDto.abbreviatedName,
    )
    this.slatForm.controls['notes'].setValue(this.slatAssessmentDataDto.notes)
  }

  checkToolsAccess() {
    this._hasAccess.flipHasAccess = this.commonService.clientHasAccessToFlip(
      this._client,
    )
    this._hasAccess.reddiHasAccess = this.commonService.clientHasAccessToReddi(
      this._client,
    )
    this._hasAccess.slatHasAccess = this.commonService.clientHasAccessToSlat(
      this._client,
    )
  }

  checkIndicators() {
    this._totalReviews = this.commonService.returnReviewIndicator(this._client)
  }

  checkForOneSubTest() {
    const groups = this.slatAssessmentDataDto?.groups ?? []
    const hasOneSubTest = groups.some((group) =>
      (group.items ?? []).some(
        (item) =>
          item.score != null &&
          item.rawScore != null &&
          item.percentileRank != null &&
          item.level != null,
      ),
    )

    return !hasOneSubTest
  }

  futureDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value

      if (!value) {
        return null
      }

      if (this._dateOfBirth !== undefined) {
        const isBefore = moment(value).isBefore(this._dateOfBirth)
        if (isBefore) {
          return { inPast: true }
        }
      }

      // isAfter() will default to current date time and the field doesn't use time
      // so value = 1/1/2020 00:00:00 now = 1/1/2020 01:38:21
      return moment(value).isAfter() ? { inFuture: true } : null
    }
  }

  previousRoute() {
    const prevUrl = this.previousRouteService.getPreviousUrl()
    if (prevUrl) {
      this.previousRouteService.goToPreviousUrl(prevUrl)
    }
  }

  getAssessmentErrorText(): string {
    const from = formatDate(this._dateOfBirth, 'dd/MM/yyyy', 'en')
    const to = formatDate(new Date(), 'dd/MM/yyyy', 'en')
    return 'Date should be between ' + from + ' and ' + to
  }
}
